import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import QRCode from 'qrcode'
import { unitId, actionId } from '@/annotations'
import { AuthCard } from '@/components/AuthCard'
import { AuthCardBrand } from '@/components/AuthCardBrand'
import { AuthCardLead } from '@/components/AuthCardLead'
import { Field } from '@/components/Field'
import { FieldStack } from '@/components/FieldStack'
import { OtpInput } from '@/components/OtpInput'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Alert } from '@/components/Alert'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { setAccessToken } from '@/data/store'
import { AUTH_BRAND, A_MFAENROL_COPY } from '@/copy/registry'
import { FormError, RateLimitedNotice, UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'

/**
 * `a-mfaenrol` — mandatory enrolment (family rule A1). "The whole string is assembled
 * server-side… the client renders it and never composes it" (`FactorEnrolStartResult`) — so this
 * screen turns `provisioning_uri` into a QR code image and nothing else; there is no manual-entry
 * fallback because the contract carries no separate manual-key field for one (a gap reported in
 * this pass's write-up, not worked around by parsing the URI's query string here).
 *
 * `identityStartFactorEnrolment` "fires on entry" (process/09-SCREEN-SPECS/a-mfaenrol.md, Fired
 * without a control).
 */
export function AMfaenrol({
  continuation,
  email,
  onEnrolled,
}: {
  continuation?: string
  email: string
  onEnrolled: () => void
}) {
  const [code, setCode] = useState('')
  const [qrDataUrl, setQrDataUrl] = useState<string>()

  const start = useQuery({
    queryKey: ['factor-enrol-start', continuation ?? 'session'],
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.POST('/auth/mfa/enrol/start', {
        body: continuation ? { continuation } : {},
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  useEffect(() => {
    if (!start.data?.provisioning_uri) return
    let cancelled = false
    QRCode.toDataURL(start.data.provisioning_uri).then((url) => {
      if (!cancelled) setQrDataUrl(url)
    })
    return () => {
      cancelled = true
    }
  }, [start.data?.provisioning_uri])

  const verify = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const enrolContinuation = start.data?.continuation ?? continuation
      const { data, error } = await client.POST('/auth/mfa/enrol/verify', {
        body: enrolContinuation ? { code, continuation: enrolContinuation } : { code },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
    onSuccess: (data) => {
      if (data && 'access_token' in data) setAccessToken(data.access_token)
      onEnrolled()
    },
  })

  const startProblem = start.error instanceof ApiProblemError ? start.error.problem : undefined
  const verifyProblem = verify.error instanceof ApiProblemError ? verify.error.problem : undefined

  return (
    <AuthCard unitId={unitId('a-mfaenrol')}>
      <AuthCardBrand label={AUTH_BRAND} />
      <h3 className="m-0 mb-3 text-section font-semibold">{A_MFAENROL_COPY.heading}</h3>
      <AuthCardLead>{A_MFAENROL_COPY.lead}</AuthCardLead>

      <Alert variant="info">{A_MFAENROL_COPY.reasonTemplate(email)}</Alert>

      {startProblem ? (
        isUnexpectedRefusal(startProblem.refusal_kind) ? (
          <UnexpectedRefusal problem={startProblem} />
        ) : (
          <FormError>{startProblem.title}</FormError>
        )
      ) : qrDataUrl ? (
        <div className="flex justify-center my-9">
          <img src={qrDataUrl} alt="Authenticator enrolment QR code" width={160} height={160} />
        </div>
      ) : (
        <div className="flex justify-center my-9 text-small text-muted">Loading…</div>
      )}

      <FieldStack>
        <Field label={A_MFAENROL_COPY.codeFieldLabel}>
          <OtpInput value={code} onChange={setCode} />
        </Field>
      </FieldStack>

      {verifyProblem && <AMfaenrolRefusal problem={verifyProblem} />}

      <Button
        data-act={actionId('factor.enrolVerify')}
        full
        disabled={verify.isPending || code.length < 6 || !qrDataUrl}
        onClick={() => verify.mutate()}
      >
        {verify.isPending ? 'Confirming…' : A_MFAENROL_COPY.submitButton}
      </Button>

      <Note>{A_MFAENROL_COPY.noRecoveryCodeNote}</Note>
    </AuthCard>
  )
}

function AMfaenrolRefusal({ problem }: { problem: Problem }) {
  switch (problem.refusal_kind) {
    case 'CREDENTIAL_REJECTED':
      return <FormError>{A_MFAENROL_COPY.rejectedCopy}</FormError>
    case 'VALIDATION_FAILED':
      return <FormError>{problem.errors?.map((e) => e.message).join(' ') ?? problem.title}</FormError>
    case 'RATE_LIMITED':
      return <RateLimitedNotice problem={problem} />
    default:
      return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <FormError>{problem.title}</FormError>
  }
}
